import { api } from '@goaltree/backend/convex/_generated/api'
import { cn } from '@goaltree/ui/lib/utils'
import { useQuery } from 'convex/react'
import { type ReactNode, useEffect, useLayoutEffect, useRef, useState } from 'react'
import { useTranslation } from 'react-i18next'

import { useIsMobile } from '@/hooks/use-mobile'
import { usePagerHeader } from '@/hooks/use-pager-header'
import { useSwipePager } from '@/hooks/use-swipe-pager'
import { keyToDate, todayKey } from '@/lib/day'
import { useViewStore } from '@/store/view.store'

export const TODAY_TABS = ['today', 'friends', 'groves'] as const
export type TodayTab = (typeof TODAY_TABS)[number]

export const TODAY_TAB_LABEL: Record<TodayTab, string> = {
  today: 'nav.today',
  friends: 'todayTabs.feed',
  groves: 'todayTabs.groups',
}

// Inactive titles: text-xl drawn as a scaled text-3xl, as on Everyday.
const TAB_INACTIVE_SCALE = 20 / 30
const TAB_GAP = 16

function useFeedNewest() {
  const feed = useQuery(api.activity.getFeed, {})
  return feed?.cards.find((c) => !c.isMe)?.createdAt ?? 0
}

export function useTodayTabDots(tab: TodayTab | undefined): Record<TodayTab, boolean> {
  const newest = useFeedNewest()
  const groveUnread = useQuery(api.groves.unreadTotal) ?? 0
  const seenAt = useViewStore((s) => s.friendsSeenAt)
  return {
    today: false,
    friends: tab !== 'friends' && newest > seenAt,
    groves: tab !== 'groves' && groveUnread > 0,
  }
}

function useTabDots(tab: TodayTab): Record<TodayTab, boolean> {
  const newest = useFeedNewest()
  const seenAt = useViewStore((s) => s.friendsSeenAt)
  const setSeenAt = useViewStore((s) => s.setFriendsSeenAt)
  useEffect(() => {
    if (tab === 'friends' && newest > seenAt) setSeenAt(newest)
  }, [tab, newest, seenAt, setSeenAt])
  return useTodayTabDots(tab)
}

// Today · Friends · Groves: Everyday-style zooming titles over a swipeable track.
// Panes mount on first visit and stay mounted; off-screen ones are only visible
// mid-swipe/settle. Each pane puts `header` at the top of its scroller.
export function TodayTabs({
  tab,
  onTab,
  panes,
}: {
  tab: TodayTab
  onTab: (tab: TodayTab) => void
  panes: Record<TodayTab, (header: ReactNode) => ReactNode>
}) {
  const { t, i18n } = useTranslation()
  const isMobile = useIsMobile()
  const dots = useTabDots(tab)
  const index = TODAY_TABS.indexOf(tab)
  const [visited, setVisited] = useState<TodayTab[]>([tab])
  const [swiping, setSwiping] = useState(false)
  const [settling, setSettling] = useState(false)
  const settleTimer = useRef<ReturnType<typeof setTimeout>>(undefined)
  const pagerRef = useRef<HTMLDivElement>(null)
  const trackRef = useRef<HTMLDivElement>(null)
  const underlineRef = useRef<HTMLSpanElement>(null)
  const panelRefs = useRef<(HTMLDivElement | null)[]>([])
  const btnRefs = useRef<(HTMLButtonElement | null)[]>([])
  const textRefs = useRef<(HTMLSpanElement | null)[]>([])
  // Natural (unscaled) title widths: tabs are absolutely placed and only ever
  // transformed, so a drag stays on the compositor.
  const [widths, setWidths] = useState<number[]>([])
  // Widths are measured in a layout effect, so the first paint is already in place;
  // transitions stay off until then so titles don't slide in from x=0.
  const [tabsReady, setTabsReady] = useState(false)

  const settle = () => {
    setSettling(true)
    clearTimeout(settleTimer.current)
    settleTimer.current = setTimeout(() => setSettling(false), 350)
  }
  useEffect(() => () => clearTimeout(settleTimer.current), [])
  const prevTab = useRef(tab)
  useLayoutEffect(() => {
    if (prevTab.current === tab) return
    float(TODAY_TABS.indexOf(prevTab.current))
    prevTab.current = tab
    moveHeader(index, true)
    land()
    setVisited((v) => (v.includes(tab) ? v : [...v, tab]))
    settle()
  }, [tab])

  useLayoutEffect(() => {
    let raf = 0
    const measure = () => {
      const next = textRefs.current.map((el) => el?.offsetWidth ?? 0)
      setWidths((prev) =>
        prev.length === next.length && prev.every((w, i) => w === next[i]) ? prev : next,
      )
      if (next.every((w) => w > 0))
        raf = requestAnimationFrame(() => {
          raf = requestAnimationFrame(() => setTabsReady(true))
        })
    }
    measure()
    const ro = new ResizeObserver(measure)
    for (const el of textRefs.current) if (el) ro.observe(el)
    return () => {
      ro.disconnect()
      cancelAnimationFrame(raf)
    }
  }, [i18n.language])

  // Scale/offset/underline at a continuous position (index − drag/width), so the
  // zoom tracks a swipe and lands exactly on the settled layout.
  const tabGeom = (pos: number) => {
    const scale = TODAY_TABS.map(
      (_, i) => 1 - (1 - TAB_INACTIVE_SCALE) * Math.min(1, Math.abs(i - pos)),
    )
    const w = TODAY_TABS.map((_, i) => (widths[i] ?? 0) * scale[i])
    const left = w.map((_, i) => w.slice(0, i).reduce((x, wi) => x + wi + TAB_GAP, 0))
    const a = Math.floor(pos)
    const b = Math.min(a + 1, TODAY_TABS.length - 1)
    const f = pos - a
    return {
      tab: (i: number) => `translateX(${left[i]}px) scale(${scale[i]})`,
      underline: `translateX(${left[a] + (left[b] - left[a]) * f}px) scaleX(${(w[a] + (w[b] - w[a]) * f) / 100})`,
    }
  }
  const paintTabs = (pos: number) => {
    const g = tabGeom(pos)
    btnRefs.current.forEach((btn, i) => {
      if (btn) btn.style.transform = g.tab(i)
    })
    if (underlineRef.current) underlineRef.current.style.transform = g.underline
  }
  const scrollerOf = (i: number) =>
    panelRefs.current[i]?.querySelector<HTMLElement>('[data-pane-scroll]') ?? null
  const {
    headerRef,
    move: moveHeader,
    float,
    land,
  } = usePagerHeader(pagerRef, scrollerOf, TODAY_TABS.length)
  const geom = tabGeom(index)
  const trackAt = (i: number, dx = 0) => `translateX(calc(${-i * 100}% + ${dx}px))`

  useSwipePager(pagerRef, {
    enabled: isMobile,
    count: TODAY_TABS.length,
    index: () => index,
    onLock: () => {
      float(index)
      pagerRef.current?.setAttribute('data-dragging', '')
      clearTimeout(settleTimer.current)
      const near = TODAY_TABS.filter((_, j) => Math.abs(j - index) <= 1)
      setVisited((v) => (near.every((tk) => v.includes(tk)) ? v : [...new Set([...v, ...near])]))
      setSwiping(true)
    },
    onDrag: (dx, width) => {
      const pos = Math.max(0, Math.min(TODAY_TABS.length - 1, index - dx / width))
      paintTabs(pos)
      moveHeader(pos)
      if (trackRef.current) trackRef.current.style.transform = trackAt(index, dx)
    },
    onRelease: (target) => {
      pagerRef.current?.removeAttribute('data-dragging')
      paintTabs(target)
      moveHeader(target, true)
      land()
      if (trackRef.current) trackRef.current.style.transform = trackAt(target)
      setSwiping(false)
      settle()
      if (target !== index) onTab(TODAY_TABS[target])
    },
  })

  const expanded = swiping || settling
  // The overlay copy owns the refs that drags paint and widths are measured from.
  const titles = (overlay: boolean) => (
    <>
      <p className="mb-1 text-xs font-semibold uppercase tracking-widest text-muted-foreground">
        {new Intl.DateTimeFormat(i18n.language, {
          weekday: 'short',
          month: 'short',
          day: 'numeric',
        }).format(keyToDate(todayKey()))}
      </p>
      <div role="tablist" className={cn('relative h-12', !tabsReady && '[&_*]:transition-none!')}>
        {TODAY_TABS.map((tk, i) => (
          <button
            key={tk}
            ref={
              overlay
                ? (el) => {
                    btnRefs.current[i] = el
                  }
                : undefined
            }
            type="button"
            role="tab"
            aria-selected={tk === tab}
            onClick={() => (tk === tab ? undefined : onTab(tk))}
            style={{ transform: geom.tab(i) }}
            className="absolute bottom-3 left-0 origin-bottom-left cursor-pointer rounded-md text-left outline-none transition-transform duration-300 ease-out will-change-transform focus-visible:ring-2 focus-visible:ring-ring/50 group-data-[dragging]/pager:transition-none motion-reduce:transition-none"
          >
            <span
              ref={
                overlay
                  ? (el) => {
                      textRefs.current[i] = el
                    }
                  : undefined
              }
              className={cn(
                'block whitespace-nowrap text-3xl leading-9 font-black transition-colors duration-300 ease-out motion-reduce:transition-none',
                tk === tab
                  ? 'text-foreground'
                  : 'text-muted-foreground/50 hover:text-muted-foreground',
              )}
            >
              {t(TODAY_TAB_LABEL[tk])}
            </span>
            {dots[tk] && (
              <span
                role="status"
                aria-label={t('today.tabNew')}
                className="absolute top-1.5 -right-3 size-2.5 rounded-full bg-primary"
              />
            )}
          </button>
        ))}
        <span
          ref={overlay ? underlineRef : undefined}
          aria-hidden
          className="absolute bottom-0 left-0 h-[3px] w-[100px] origin-left rounded-full bg-primary-text transition-transform duration-300 ease-out will-change-transform group-data-[dragging]/pager:transition-none motion-reduce:transition-none"
          style={{ transform: geom.underline }}
        />
      </div>
    </>
  )
  const paneHeader = (
    <div data-pane-tabs className="shrink-0 px-4 pt-4">
      <div className="mx-auto max-w-(--page-col)">{titles(false)}</div>
    </div>
  )
  return (
    <div
      ref={pagerRef}
      data-hdr-pager
      className="group/pager relative isolate flex min-h-0 flex-1 touch-pan-y flex-col overflow-clip"
    >
      <div
        ref={headerRef}
        data-tabs-bar
        className="pointer-events-none absolute inset-x-0 top-(--app-hdr-h) z-10 bg-background px-4 pt-4"
        onWheel={(e) => scrollerOf(index)?.scrollBy({ top: e.deltaY })}
      >
        <div className="pointer-events-auto mx-auto max-w-(--page-col)">{titles(true)}</div>
      </div>
      <div className="flex min-h-0 flex-1">
        <div
          ref={trackRef}
          className="flex w-full transition-transform duration-250 ease-out group-data-[dragging]/pager:transition-none motion-reduce:transition-none"
          style={{ transform: trackAt(index) }}
        >
          {TODAY_TABS.map((tk, i) => (
            <div
              key={tk}
              ref={(el) => {
                panelRefs.current[i] = el
              }}
              role="tabpanel"
              inert={tk !== tab}
              className={cn(
                'flex min-h-0 w-full shrink-0 flex-col',
                tk !== tab && !expanded && 'invisible',
              )}
            >
              {(tk === tab || visited.includes(tk)) && panes[tk](paneHeader)}
            </div>
          ))}
        </div>
      </div>
    </div>
  )
}
